/**
 * ArtifactRenderer - Dynamic Artifact Type Switcher
 * TASK-286, TASK-289: Agentic Chat Artifact Rendering
 *
 * Routes artifacts to appropriate rendering components based on type.
 */

import React, { Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import type { Artifact } from '../../../types/chat.types';
import ArtifactWrapper from './ArtifactWrapper';
import ArtifactErrorBoundary from './ArtifactErrorBoundary';

// Lazy load artifact components for better performance
const ChartArtifact = React.lazy(() => import('./ChartArtifact'));
const TableArtifact = React.lazy(() => import('./TableArtifact'));
const MetricArtifact = React.lazy(() => import('./MetricArtifact'));
const ClientCardArtifact = React.lazy(() => import('./ClientCardArtifact'));
const ClarificationArtifact = React.lazy(() => import('./ClarificationArtifact'));
const ExportDownloadArtifact = React.lazy(() => import('./ExportDownloadArtifact'));

interface ArtifactRendererProps {
  artifact: Artifact;
}

/**
 * Loading fallback for lazy-loaded artifacts
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
  </div>
);

/**
 * Render appropriate artifact component based on type
 */
const ArtifactRenderer: React.FC<ArtifactRendererProps> = ({ artifact }) => {
  const renderContent = () => {
    switch (artifact.type) {
      case 'pie_chart':
      case 'bar_chart':
      case 'line_chart':
        return <ChartArtifact artifact={artifact} />;

      case 'data_table':
        return <TableArtifact artifact={artifact} />;

      case 'metric_cards':
        return <MetricArtifact artifact={artifact} />;

      case 'client_card':
        return <ClientCardArtifact artifact={artifact} />;

      case 'clarification':
        return <ClarificationArtifact artifact={artifact} />;

      case 'export_download':
        return <ExportDownloadArtifact artifact={artifact} />;

      case 'summary':
        return (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {artifact.data.text as string}
            </p>
          </div>
        );

      default:
        // Fallback for unknown types - show raw data
        return (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">
              Unknown artifact type: {artifact.type}
            </p>
            <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto">
              {JSON.stringify(artifact.data, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <ArtifactErrorBoundary artifactId={artifact.id}>
      <ArtifactWrapper artifact={artifact}>
        <Suspense fallback={<LoadingFallback />}>
          {renderContent()}
        </Suspense>
      </ArtifactWrapper>
    </ArtifactErrorBoundary>
  );
};

export default ArtifactRenderer;
